// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { Button, Flex, Typography } from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';
import { useCallback, useState } from 'react';

import {
  OrderCompleteCheckIcon,
  OrderCompleteContainer,
  OrderCompleteSubContainer,
  OrderRatingFiveStars,
} from '../../components';
import type { RootLoaderType } from '../../data-access';
import { useGetKiosk } from '../../data-access';
import {
  useKioskSpacing,
  useShouldKioskShowGold,
  useTypedRouteLoaderData,
} from '../../hooks';
import { useCartInfoForTracking, useLogoutUser } from '../../providers';
import { trackCustomerRating } from '../../util';

const PaddedFlex = styled(Flex)({
  paddingLeft: '15%',
  paddingRight: '15%',
});

export const OrderComplete = () => {
  const {
    kiosk: initialKiosk,
    kioskId,
    store,
  } = useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: kiosk } = useGetKiosk({
    kioskId,
    initialData: initialKiosk,
  });
  const showGold = useShouldKioskShowGold();
  const { cartInfo, setCartInfo } = useCartInfoForTracking();
  const { selectedBankAccount, signedInOrder, ...cartInfoForTracking } =
    cartInfo ?? { selectedBankAccount: null, signedInOrder: null };

  const hasCashBack =
    selectedBankAccount && (cartInfo?.cart?.brand_discounts_total ?? 0) > 0;

  const { logoutAndReset } = useLogoutUser();

  const [rating, setRating] = useState<number>(0);

  const { isIpad } = useKioskSpacing();

  const queueNumber = localStorage.getItem('queueNumber');

  const onRatingPress = useCallback(
    (index: number) => {
      setRating(index + 1);
      trackCustomerRating({
        kiosk,
        store,
        starRating: index + 1,
        ...cartInfoForTracking,
      });
      setCartInfo(null);
    },
    [cartInfoForTracking, kiosk, setCartInfo, store]
  );

  return (
    <OrderCompleteContainer
      alignItems="center"
      flexDirection="column"
      justifyContent="center"
      width="100%"
      height="100%"
    >
      <Flex mb={isIpad ? 48 : 64}>
        <OrderCompleteCheckIcon
          width={isIpad ? 120 : 220}
          height={isIpad ? 120 : 220}
          color="text-info"
        />
      </Flex>

      {queueNumber && (
        <Typography
          color="text-info"
          mb={24}
          variant="title"
          style={{ fontSize: isIpad ? '24px' : '36px' }}
          as="h1"
        >
          Order #{queueNumber}
        </Typography>
      )}

      <PaddedFlex flexDirection="column">
        <Typography
          color="text-info"
          textAlign="center"
          variant="huge-bold"
          as="h2"
        >
          {kiosk?.checkout_confirmation_message ||
            'All set! Grab your order at the counter.'}
        </Typography>

        {showGold && signedInOrder && (
          <>
            <Typography
              color="text-info"
              textAlign="center"
              variant="title"
              style={{
                fontSize: isIpad ? '24px' : '36px',
                lineHeight: isIpad ? '32px' : '44px',
              }}
              mt={24}
              as="p"
            >
              You've been signed out of your account for your privacy.
            </Typography>
            {hasCashBack && (
              <OrderCompleteSubContainer
                alignItems="center"
                flexDirection="column"
                mt={48}
                p={isIpad ? 24 : 48}
                gap={isIpad ? 24 : 40}
              >
                <Typography
                  textAlign="center"
                  color="text-info"
                  variant="section-bold"
                  style={{
                    fontSize: isIpad ? '20px' : '24px',
                    lineHeight: isIpad ? '24px' : '30px',
                  }}
                  as="h2"
                >
                  Your estimated Jane Gold cash back
                </Typography>
                <Typography
                  textAlign="center"
                  color="text-info"
                  variant="huge-bold"
                  as="p"
                >
                  {formatCurrency(cartInfo?.cart?.brand_discounts_total)}
                </Typography>
                <Typography
                  textAlign="center"
                  color="text-info"
                  variant="mini"
                  style={{
                    fontSize: isIpad ? '13px' : '18px',
                    lineHeight: isIpad ? '16px' : '22px',
                  }}
                  as="p"
                >
                  Your brand-sponsored cash back reward will be deposited to
                  your linked bank account at {selectedBankAccount} in 1-3 days.
                </Typography>
              </OrderCompleteSubContainer>
            )}
          </>
        )}

        <OrderCompleteSubContainer
          alignItems="center"
          flexDirection="column"
          mt={48}
          px={isIpad ? 16 : 48}
          py={isIpad ? 24 : 48}
        >
          <Typography
            textAlign="center"
            color="text-info"
            mb={isIpad ? 24 : 40}
            variant="section-bold"
            style={{
              fontSize: isIpad ? '20px' : '24px',
            }}
            as="h2"
          >
            {rating
              ? 'Thanks for your feedback!'
              : isIpad
                ? 'How was your order\nexperience?'
                : 'How was your kiosk experience today?'}
          </Typography>
          <Flex flexDirection="row">
            <OrderRatingFiveStars
              onRatingPress={onRatingPress}
              rating={rating}
              isMobile={isIpad ? true : false}
            />
          </Flex>
        </OrderCompleteSubContainer>

        <Flex mt={isIpad ? 48 : 64} justifyContent="center">
          <Button
            full={isIpad ? true : false}
            label={
              <Typography
                color="text-inverse"
                variant={`${isIpad ? 'body' : 'title'}-bold`}
              >
                Done
              </Typography>
            }
            size={isIpad ? 'default' : 'jumbo'}
            onClick={() => logoutAndReset()}
            variant="secondary"
          />
        </Flex>
      </PaddedFlex>
    </OrderCompleteContainer>
  );
};
